<script setup>
import { ref, watch } from 'vue'
import Dialog from 'primevue/dialog'
import InputText from 'primevue/inputtext'
import Button from 'primevue/button'
import Message from 'primevue/message'
import { createRole, updateRole } from '../api/roles.service'

const props = defineProps({
  visible: {
    type: Boolean,
    default: false
  },
  role: {
    type: Object,
    default: null
  },
  mode: {
    type: String,
    default: 'create'
  }
})

const emit = defineEmits(['update:visible', 'saved'])

const roleName = ref('')
const loading = ref(false)
const nameError = ref('')
const formError = ref('')

watch(() => props.visible, (newVal) => {
  if (newVal && props.role && props.mode === 'edit') {
    roleName.value = props.role.name
    nameError.value = ''
    formError.value = ''
  } else if (newVal && props.mode === 'create') {
    roleName.value = ''
    nameError.value = ''
    formError.value = ''
  }
})

function clearNameError() {
  nameError.value = ''
}

async function handleSave() {
  if (!roleName.value.trim()) {
    nameError.value = 'Название роли не может быть пустым'
    return
  }

  loading.value = true
  nameError.value = ''
  formError.value = ''

  try {
    if (props.mode === 'create') {
      await createRole({ name: roleName.value.trim() })
    } else if (props.mode === 'edit' && props.role) {
      await updateRole(props.role.id, { name: roleName.value.trim() })
    }
    
    emit('saved')
    closeDialog()
  } catch (err) {
    console.error('Ошибка при сохранении роли', err)
    
    nameError.value = err.fieldErrors?.Name || err.fieldErrors?.name || ''
    if (!nameError.value) {
      formError.value = 'Не удалось сохранить роль. Попробуйте ещё раз.'
    }
  } finally {
    loading.value = false
  }
}

function closeDialog() {
  emit('update:visible', false)
  roleName.value = ''
  nameError.value = ''
  formError.value = ''
}
</script>

<template>
  <Dialog
    :visible="visible"
    :header="mode === 'create' ? 'Создание роли' : 'Редактирование роли'"
    :modal="true"
    :style="{ width: 'min(450px, 95vw)' }"
    @update:visible="closeDialog"
  >
    <div class="role-form">
      <div class="field">
        <label for="roleName">Название роли</label>
        <InputText
          id="roleName"
          v-model="roleName"
          placeholder="Введите название роли"
          :invalid="Boolean(nameError)"
          @input="clearNameError"
          autofocus
        />
        <small v-if="nameError" class="field-error">
          {{ nameError }}
        </small>
      </div>

      <Message
        v-if="formError"
        severity="error"
        class="error-message"
      >
        {{ formError }}
      </Message>

      <div class="form-actions">
        <Button
          label="Отмена"
          icon="pi pi-times"
          class="p-button-text"
          @click="closeDialog"
        />
        <Button
          :label="mode === 'create' ? 'Создать' : 'Сохранить'"
          icon="pi pi-check"
          :loading="loading"
          @click="handleSave"
        />
      </div>
    </div>
  </Dialog>
</template>

<style scoped>
.role-form {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.field {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.field label {
  font-weight: 600;
  color: #333;
}

:deep(.p-inputtext) {
  width: 100%;
}

.error-message {
  margin: 0;
}

.field-error {
  color: var(--p-form-field-invalid-color, #e24c4c);
}

.form-actions {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 8px;
}
</style>
